import { useQuery, useMutation } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import {
  cancleSubscriptionApi,
  fetchActivePlan,
  fetchPlanDetails,
  fetchPlanEntitlement,
  createPendingApi,
  fetchVendor,
  fetchVendorPaymentDetails,
  fetchSubscriptionStatus,
  createSubscription,
  createTransactionApi,
  fetchTransactionStatus,
  fetchPriceBreakup,
  fetchSubscriptionTransactions,
  fetchSubscribeButtonStatus,
  checkPaymentStatus
} from 'shared-ui/api'
import { QUERY_KEY } from 'shared-ui/utils/constants'
import { isAndroidApp } from 'shared-ui/utils/helper'
import { useLanguage } from 'shared-ui/utils/languageProvider'

export const usePlanDetails = (
  entitlement_groups,
  params = {},
  options = {}
) => {
  const { language } = useLanguage()
  return useQuery({
    queryKey: [QUERY_KEY.GET_PLAN, language, ...entitlement_groups],
    queryFn: async () => {
      if (!entitlement_groups) throw new Error('Entitlement group is required')
      return await fetchPlanDetails(entitlement_groups, language)
    },
    ...options
  })
}

export const useActivePlan = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEY.GET_ACTIVE_PLAN],
    queryFn: async () => {
      const resp = await fetchActivePlan()
      return resp
    },
    ...options
  })
}

export const useCancleSubscription = (options = {}) => {
  return useMutation({
    mutationKey: [QUERY_KEY.CANCLE_SUBSCRIPTION],
    mutationFn: async ({ val }) => {
      let subscription_ids = val
      const resp = await cancleSubscriptionApi([subscription_ids])
      return resp
    },
    ...options
  })
}

export const usePendingSubscription = (options = {}) => {
  return useMutation({
    mutationKey: [QUERY_KEY.PENDING_SUBSCRIPTION],
    mutationFn: async (payload) => {
      const resp = await createPendingApi(payload)
      return resp
    },
    ...options
  })
}

export const useProductDetails = (options = {}) => {
  const { language } = useLanguage()
  return useQuery({
    queryKey: [QUERY_KEY.GET_PRODUCET_DETAILS, language],
    queryFn: async () => {
      const resp = await fetchPlanDetails([], language)
      return resp
    },
    ...options
  })
}

export const useFetchPrice = (
  product_id,
  discount_code,
  isAuthenticated,
  options = {}
) => {
  const { language } = useLanguage()
  return useQuery({
    queryKey: [QUERY_KEY.GET_PRICE_BREAKUP, product_id, discount_code, language],
    queryFn: () => fetchPriceBreakup(product_id, discount_code, language),
    enabled: isAndroidApp ? true : isAuthenticated && !!product_id,
    retry: false, // ✅ show error immediately

    ...options
  })
}

export const usePriceEntitlement = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEY.GET_ENTITLEMENT],
    queryFn: async () => {
      const resp = await fetchPlanEntitlement()
      return resp
    },
    ...options
  })
}

export const useVendorDetails = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEY.GET_PAYMENT_VENDOR],
    queryFn: async () => {
      const resp = await fetchVendor()
      return resp
    },
    ...options
  })
}

export const useVendorPaymentDetails = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEY.GET_VENDOR_PAYMENT_DETAILS],
    queryFn: async () => {
      const resp = await fetchVendorPaymentDetails()
      return resp
    },
    ...options
  })
}

export const useTransactionStatus = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEY.GET_TRANSACTION_STATUS],
    queryFn: async () => {
      const resp = await fetchTransactionStatus()
      return resp
    },
    ...options
  })
}

export const useSubscriptionStatus = (options = {}) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
  const { language } = useLanguage()
  return useQuery({
    queryKey: [QUERY_KEY.GET_SUBSCRIPTION_STATUS, language],
    queryFn: async () => {
      const resp = await fetchSubscriptionStatus(language)
      return resp
    },
    enabled: isAuthenticated,
    ...options
  })
}
export const useSubscriptionTransactions = (options = {}) => {
  const { language } = useLanguage()
  return useQuery({
    queryKey: [QUERY_KEY.GET_SUBSCRIPTION_TRANSACTIONS, language],
    queryFn: async () => {
      const resp = await fetchSubscriptionTransactions(language)
      return resp
    },
    ...options
  })
}

export const useCreateSubscription = (options = {}) => {
  return useMutation({
    mutationKey: [QUERY_KEY.CREATE_SUBSCRIPTION],
    mutationFn: async (payload) => {
      const {androidCurrentCountry, authTokenForAndroid, ...data } = payload
      return await createSubscription(data, authTokenForAndroid,androidCurrentCountry)
    },
    ...options
  })
}

export const useCreateTransaction = (options = {}) => {
  return useMutation({
    mutationKey: [QUERY_KEY.CREATE_TRANSACTION],
    mutationFn: async (variables) => {
      const resp = await createTransactionApi(variables)
      return resp
    },
    ...options
  })
}
// export const useSubscriptionButtonStatus = (options = {}) => {
//   return useQuery({
//     queryKey: [QUERY_KEY.GET_SUBSCRIPTION_BUTTON_STATUS],
//     queryFn: async () => {
//       const resp = await fetchSubscribeButtonStatus()
//       return resp
//     },
//     ...options
//   })
// }

export const useSubscriptionButtonStatus = (isAuthenticated) => {
  const { language } = useLanguage()
  return useQuery({
    queryKey: [QUERY_KEY.GET_SUBSCRIPTION_BUTTON_STATUS, language],
    queryFn: () => fetchSubscribeButtonStatus(language),
    enabled: isAuthenticated,
    staleTime: 0,
    cacheTime: 10 * 60 * 1000
  })
}

export const useCheckPaymentStatus = (options = {}) => {
  return useMutation({
    mutationKey: [QUERY_KEY.CHECK_PAYMENT_STATUS],
    mutationFn: async (variables) => {
      const resp = await checkPaymentStatus(variables)
      return resp
    },
    ...options
  })
}
